var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};

// src/components/CursorOverlay.tsx
import React from "react";
import {
  usePlateSelectors
} from "@udecode/plate-common";

// src/hooks/useCursorOverlayPositions.ts
import { useCallback as useCallback3, useMemo, useRef as useRef2, useState as useState2 } from "react";
import {
  useEditorRef,
  useIsomorphicLayoutEffect
} from "@udecode/plate-common";

// src/hooks/useRefreshOnResize.ts
import { useCallback as useCallback2, useEffect as useEffect2 } from "react";

// src/hooks/useRequestReRender.ts
import { useCallback, useEffect, useRef, useState } from "react";
var useRequestReRender = () => {
  const [, setUpdateCounter] = useState(0);
  const animationFrameRef = useRef(null);
  const requestReRender = useCallback((immediate = false) => {
    if (animationFrameRef.current && !immediate) {
      return;
    }
    if (!immediate) {
      animationFrameRef.current = requestAnimationFrame(() => {
        setUpdateCounter((state) => state + 1);
        animationFrameRef.current = null;
      });
      return;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setUpdateCounter((state) => state + 1);
  }, []);
  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  });
  useEffect(
    () => () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    },
    []
  );
  return requestReRender;
};

// src/hooks/useRefreshOnResize.ts
var useRefreshOnResize = ({
  containerRef,
  refreshOnResize,
  selectionRectCache
}) => {
  const requestReRender = useRequestReRender();
  const refresh = useCallback2(
    (sync = false) => {
      selectionRectCache.current = /* @__PURE__ */ new WeakMap();
      requestReRender(sync);
    },
    [requestReRender, selectionRectCache]
  );
  useEffect2(() => {
    if (!refreshOnResize || !(containerRef == null ? void 0 : containerRef.current)) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => refresh());
    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [containerRef, refresh, refreshOnResize]);
  return {
    refresh
  };
};

// src/queries/getCaretPosition.ts
import { Range } from "slate";
var getCaretPosition = (selectionRects, range) => {
  const isCollapsed = range && Range.isCollapsed(range);
  const isBackward = range && Range.isBackward(range);
  const anchorRect = selectionRects[isBackward ? 0 : selectionRects.length - 1];
  if (!anchorRect) {
    return null;
  }
  return {
    height: anchorRect.height,
    top: anchorRect.top,
    left: anchorRect.left + (isBackward || isCollapsed ? 0 : anchorRect.width)
  };
};

// src/queries/getCursorOverlayState.ts
var getCursorOverlayState = ({
  cursors: cursorStates,
  selectionRects
}) => {
  if (!cursorStates)
    return [];
  return Object.entries(cursorStates).map(([key, cursorState]) => {
    var _a, _b;
    const selection = (_a = cursorState == null ? void 0 : cursorState.selection) != null ? _a : null;
    const rects = (_b = selectionRects[key]) != null ? _b : FROZEN_EMPTY_ARRAY;
    const caretPosition = selection ? getCaretPosition(rects, selection) : null;
    return __spreadProps(__spreadValues({}, cursorState), {
      selection,
      caretPosition,
      selectionRects: rects
    });
  });
};

// src/queries/getSelectionRects.ts
import {
  getNodeEntries,
  isText,
  toDOMNode,
  toDOMRange
} from "@udecode/plate-common";
import { Path, Range as Range2 } from "slate";
var getSelectionRects = (editor, {
  range,
  xOffset,
  yOffset
}) => {
  const [start, end] = Range2.edges(range);
  const domRange = toDOMRange(editor, range);
  if (!domRange) {
    return [];
  }
  const selectionRects = [];
  const textEntries = getNodeEntries(editor, {
    at: range,
    match: isText
  });
  for (const [textNode, textPath] of textEntries) {
    const domNode = toDOMNode(editor, textNode);
    if (!domNode || !domNode.parentElement) {
      return [];
    }
    const isStartNode = Path.equals(textPath, start.path);
    const isEndNode = Path.equals(textPath, end.path);
    let clientRects = null;
    if (isStartNode || isEndNode) {
      const nodeRange = document.createRange();
      nodeRange.selectNode(domNode);
      if (isStartNode) {
        nodeRange.setStart(domRange.startContainer, domRange.startOffset);
      }
      if (isEndNode) {
        nodeRange.setEnd(domRange.endContainer, domRange.endOffset);
      }
      clientRects = nodeRange.getClientRects();
    } else {
      clientRects = domNode.getClientRects();
    }
    for (let i = 0; i < clientRects.length; i++) {
      const clientRect = clientRects.item(i);
      if (!clientRect) {
        continue;
      }
      selectionRects.push({
        width: clientRect.width,
        height: clientRect.height,
        top: clientRect.top - yOffset,
        left: clientRect.left - xOffset
      });
    }
  }
  return selectionRects;
};

// src/hooks/useCursorOverlayPositions.ts
var FROZEN_EMPTY_ARRAY = Object.freeze(
  []
);
var useCursorOverlayPositions = ({
  containerRef,
  cursors: cursorStates,
  refreshOnResize = true
} = {}) => {
  const editor = useEditorRef();
  const selectionRectCache = useRef2(
    /* @__PURE__ */ new WeakMap()
  );
  const [selectionRects, setSelectionRects] = useState2({});
  const updateSelectionRects = useCallback3(() => {
    if (!(containerRef == null ? void 0 : containerRef.current))
      return;
    if (!cursorStates)
      return;
    let xOffset = 0;
    let yOffset = 0;
    if (containerRef) {
      const contentRect = containerRef.current.getBoundingClientRect();
      xOffset = contentRect.x;
      yOffset = contentRect.y;
    }
    let selectionRectsChanged = Object.keys(selectionRects).length !== Object.keys(cursorStates).length;
    const getCachedSelectionRects = ({
      cursor
    }) => {
      const range = cursor.selection;
      if (!range) {
        return FROZEN_EMPTY_ARRAY;
      }
      const cached = selectionRectCache.current.get(range);
      if (cached) {
        return cached;
      }
      const rects = getSelectionRects(editor, { range, xOffset, yOffset });
      selectionRectsChanged = true;
      selectionRectCache.current.set(range, rects);
      return rects;
    };
    const updated = Object.fromEntries(
      Object.entries(cursorStates).map(([key, cursor]) => [
        key,
        getCachedSelectionRects({
          cursor
        })
      ])
    );
    if (selectionRectsChanged) {
      setSelectionRects(updated);
    }
  }, [containerRef, cursorStates, editor, selectionRects]);
  useIsomorphicLayoutEffect(() => {
    updateSelectionRects();
  });
  const cursors = useMemo(
    () => getCursorOverlayState({
      selectionRects,
      cursors: cursorStates
    }),
    [cursorStates, selectionRects]
  );
  const { refresh } = useRefreshOnResize({
    containerRef,
    selectionRectCache,
    refreshOnResize
  });
  return { refresh, cursors };
};

// src/components/CursorOverlay.tsx
function CursorOverlayContent(_a) {
  var _b = _a, {
    classNames,
    onRenderCursor: CursorComponent,
    onRenderSelectionRect,
    onRenderCaret
  } = _b, props = __objRest(_b, [
    "classNames",
    "onRenderCursor",
    "onRenderSelectionRect",
    "onRenderCaret"
  ]);
  const { disableCaret, disableSelection } = props;
  const { cursors } = useCursorOverlayPositions(props);
  const cursorProps = {
    classNames,
    onRenderSelectionRect,
    onRenderCaret,
    disableCaret,
    disableSelection
  };
  if (!CursorComponent)
    return null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, cursors.map((cursor) => /* @__PURE__ */ React.createElement(CursorComponent, __spreadValues(__spreadValues({ key: cursor.key }, cursorProps), cursor))));
}
function CursorOverlay(props) {
  const isMounted = usePlateSelectors().isMounted();
  if (!isMounted)
    return null;
  return /* @__PURE__ */ React.createElement(CursorOverlayContent, __spreadValues({}, props));
}
export {
  CursorOverlay,
  CursorOverlayContent,
  FROZEN_EMPTY_ARRAY,
  getCaretPosition,
  getCursorOverlayState,
  getSelectionRects,
  useCursorOverlayPositions,
  useRefreshOnResize,
  useRequestReRender
};
//# sourceMappingURL=index.mjs.map